import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import React, { ChangeEvent, Component } from "react";
import { CreateOrUpdateMerchantDocument, MerchantDocument } from "../../models/MerchantModel";
import { Button } from "primereact/button";
import { merchantDocumentApiService } from "../../services/api/merchants/MerchantDocumentApiService";
import ExtendedInputText from "../../components/ExtendedInputText";
import { toastService } from "../../services/toastService";
import { Dropdown } from "primereact/dropdown";
import { SelectItem } from "../../models/SelectItem";
import { utils } from "../../utils/utils";
import { fileService } from "../../services/FileService";
import { ExtendedImage } from "../../components/Image/ExtendedImage";
import { confirmDanger } from "../../utils/confirmHelpers";
import _ from "lodash";
import { Result } from "../../models/Result";
import { isMerchantProfileUpdateAllowed } from "../../authGuard";
import { Check, FileText, Plus, X } from "lucide-react";
import { nanoid } from "nanoid";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";

interface MerchantDocumentsProps {
    merchantId: number;
    localMode: boolean;
    onLocalDocumentUpdated?: (documents: MerchantDocument[]) => void;
    merchantDocuments?: MerchantDocument[];
    valieDateLocalDocuments?: (value: boolean) => void;
    localDocumentInvalid?: boolean;
}

interface MerchantDocumentsState {
    isLoading: boolean;
    documents: MerchantDocument[];
    documentInvalid: boolean;
    showFile: any;
    fileType: string;
    addedDocumentInvalid: boolean;
    imageSourceDocument: Map<string, string>;
}

export default class MerchantDocuments extends Component<MerchantDocumentsProps, MerchantDocumentsState> {
    imageInput: HTMLInputElement | null = null;
    validationErrors: Map<string, boolean> = new Map<string, boolean>();
    documentTypes: SelectItem[] = [];
    documentStatus: SelectItem[] = [];
    signOrderDocument: SelectItem[] = [];

    constructor(props: MerchantDocumentsProps | Readonly<MerchantDocumentsProps>) {
        super(props);

        this.state = {
            documents: this.props.localMode ? this.props.merchantDocuments! : [],
            isLoading: true,
            documentInvalid: this.props.localDocumentInvalid ?? false,
            showFile: null,
            fileType: "",
            addedDocumentInvalid: false,
            imageSourceDocument: new Map<string, string>(),
        };
    }

    componentDidUpdate(prevProps: MerchantDocumentsProps) {
        if (prevProps.localDocumentInvalid !== this.props.localDocumentInvalid) {
            this.setState({ documentInvalid: this.props.localDocumentInvalid ?? false });
        }
    }

    async componentDidMount() {
        this.setState({ isLoading: true });

        const documentTypes = await merchantDocumentApiService.getMerchantDocumentsTypes();

        if (!documentTypes.success) {
            toastService.error(documentTypes.message);
            this.setState({ isLoading: false });
            return;
        }

        this.documentTypes = documentTypes.data;

        const documentStatus = await merchantDocumentApiService.getDocumentsStatus();

        if (!documentStatus.success) {
            toastService.error(documentStatus.message);
            this.setState({ isLoading: false });
            return;
        }

        this.documentStatus = documentStatus.data;

        this.setState({ isLoading: false });

        await this.loadDocuments();
    }

    loadDocuments = async () => {
        if (this.props.localMode) {
            this.generateSignOrderOptions();
            return;
        }
        this.setState({ isLoading: true });

        const documentResult = await merchantDocumentApiService.getAll(this.props.merchantId);

        if (!documentResult.success) {
            toastService.error(documentResult.message);
            this.setState({ isLoading: false });
            return;
        }

        this.generateSignOrderOptions(documentResult.data.length);

        this.setState(
            { documents: documentResult.data, isLoading: false },
            () => {
                this.preLoadDocumnetsmages();
            }
        );
    };

    generateSignOrderOptions = (length: number = 1) => {
        const totalOptions = Math.max(length, 1) * 3;
        this.signOrderDocument = Array.from({ length: totalOptions }, (_, index) => ({
            label: `${index + 1}`,
            value: (index + 1).toString()
        }));
    };


    preLoadDocumnetsmages = async () => {
        const { documents, imageSourceDocument } = this.state;
        const updatedImageSources = new Map(imageSourceDocument);

        documents.forEach((doc) => {
            if (doc.fileStorageId && utils.hasImageExtension(doc.fileStorageId) && !updatedImageSources.has(doc.fileStorageId)) {
                this.loadFile(doc.fileStorageId)
                    .then((src) => {
                        updatedImageSources.set(doc.fileStorageId!, src);
                        this.setState({ imageSourceDocument: new Map(updatedImageSources) });
                    })
                    .catch(() => {
                        console.error(`Failed to load image for ${doc.fileStorageId}`);
                    });
            }
        });
    };

    loadFile = async (fileStorageId: string): Promise<string> => {
        if (fileStorageId != null || fileStorageId != "") {
            const imagePath = await fileService.GetFile(fileStorageId);
            if (!imagePath.success) {
                toastService.error(imagePath.message);
                return "";
            }
            return utils.appendBase64Data(imagePath.data, fileStorageId);
        }
        return "";
    };

    onRowEditComplete = async (e: DataTableRowEditCompleteEvent) => {
        this.setState({ isLoading: true, documentInvalid: false, addedDocumentInvalid: false });
        const newData = e.newData as MerchantDocument;
        const fileData = (newData as any).file;
        const objToPost: CreateOrUpdateMerchantDocument = {
            fileName: newData.fileName,
            fileData: fileData,
            type: newData.documentType,
            merchantId: this.props.merchantId,
            id: newData.id,
            isForSigning: newData.isForSigning,
            signOrder: newData.signOrder,
            description: newData.description
        };

        let result: Result<MerchantDocument>;
        if (this.props.localMode) {
            result = { success: true, data: { fileStorageId: "", id: "", status: "New" } as any, message: "" } as Result<MerchantDocument>;
        } else {
            result = newData.id != 0 ? await merchantDocumentApiService.update(objToPost) : await merchantDocumentApiService.create(objToPost);
        }

        this.setState({ isLoading: false, documentInvalid: false, showFile: null });

        if (this.props.localMode) {
            const localDocuments = this.state.documents;
            newData.status = "1";
            (newData.fileData = fileData),
                (newData.type = newData.documentType),
                (newData.fileType = this.state.fileType),
                (localDocuments[e.index] = newData);
            this.props.onLocalDocumentUpdated!(localDocuments);
            this.props.valieDateLocalDocuments!(false);
            return;
        }

        if (!result.success) {
            toastService.error(result.message);
            return;
        }

        const documents = this.state.documents;

        newData.id = result.data.id;
        newData.fileStorageId = result.data.fileStorageId;
        newData.status = result.data.status;
        newData.type = newData.documentType;
        newData.fileData = fileData;
        newData.description = result.data.description;
        newData.signOrder = result.data.signOrder;
        newData.isForSigning = result.data.isForSigning;
        documents[e.index] = newData;

        this.setState({ documents: documents });
    };

    renderHeader = () => {
        return (
            <div className="table-header">
                <span className="p-input-icon-left align-right">
                    {isMerchantProfileUpdateAllowed() && (
                        <Button label="Add Document" outlined icon={<Plus size={16} />} onClick={this.addDocument} />
                    )}
                </span>
            </div>
        );
    };

    addDocument = () => {
        const documents = this.state.documents;
        if (!this.state.addedDocumentInvalid) {
            const newDoc = new MerchantDocument();
            newDoc.id = 0;
            newDoc.merchantId = this.props.merchantId;
            newDoc.documentType = this.documentTypes.find((i) => i.value == "OTH")?.value ?? "OTH";
            documents.push(newDoc);
            this.setState({ documents: documents, addedDocumentInvalid: true });
        } else {
            toastService.error("Previous document has incomplete data.");
        }
    };

    textEditor = (options: ColumnEditorOptions) => {
        return (
            <>
                <ExtendedInputText
                    type="text"
                    value={options.value}
                    onChange={(value) => options.editorCallback && options.editorCallback(value)}
                    name={options.field}
                />
                {this.state.documentInvalid && options.value === "" && (
                    <small className="p-error block">{`${utils.toPascalCase(options.field)} is required`}</small>
                )}
            </>
        );
    };

    onRowValidateDocument = (newData: any) => {
        const requiredFields = ["fileName", "documentType", "description", "signOrder"];
        const emptyFields = requiredFields.filter((field) => {
            const value = newData[field];
            if (field === "signOrder") {
                return value === 0 || utils.isNullOrEmptyString(value);
            }
            const hasFile = !!newData.fileData || !!newData.fileStorageId;

            if (field === "file") {
                return newData.id === 0 && !hasFile;
            }
            return !value || value.trim() === "";
        });


        if (emptyFields.length > 0) {
            this.setState({ documentInvalid: true });
            return false;
        }
        return true;
    };

    statusEditor = (options: ColumnEditorOptions) => {
        const list = _.filter(this.documentStatus, (c) => c.value == options.value);
        return (
            <div key={nanoid()}>
                <span>{list.length > 0 ? list[0].label : "New"}</span>
            </div>
        );
    };

    typesEditor(options: ColumnEditorOptions) {
        return (
            <div key={nanoid()}>
                <Dropdown
                    value={options.value}
                    options={this.documentTypes}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => options.editorCallback && options.editorCallback(e.value)}
                    placeholder="Select a Type"
                    itemTemplate={(option) => {
                        return <span>{option.label}</span>;
                    }}
                />
                {this.state.documentInvalid && options.value === "" && (
                    <>
                        <br></br>
                        <small className="p-error block">{`${utils.toPascalCase(options.field)} is required`}</small>
                    </>
                )}
            </div>
        );
    }

    fileEditor = (options: ColumnEditorOptions) => {
        if ((options.rowData as MerchantDocument).id == 0) {
            return (
                <>
                    <div style={{ display: "flex", alignItems: "center" }} key={nanoid()}>
                        <div key={nanoid()}>
                            <Button
                                label="Choose"
                                className="ms-1"
                                size="small"
                                icon={<Check size={16} />}
                                onClick={() => this.imageInput?.click()}
                            />
                            <input
                                ref={(el) => (this.imageInput = el)}
                                type="file"
                                style={{ display: "none" }}
                                accept="image/jpeg, image/png, application/pdf"
                                onChange={(event) => this.onImageSelected(event, options.editorCallback!)}
                                required={true}
                            />
                        </div>
                        {this.state.fileType.includes("pdf") ? (
                            <div key={nanoid()}>
                                <FileText size={40} />
                            </div>
                        ) : this.state.showFile ? (
                            <img src={this.state.showFile} width={"30px"} style={{ marginLeft: "10px" }} />
                        ) : (
                            <div className="w-[30px] h-[30px] ml-2.5 flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 rounded">
                                <FileText size={16} className="text-neutral-400" />
                            </div>
                        )}
                    </div>
                    {this.state.documentInvalid && !this.state.showFile && this.state.documents.find((i) => i.fileData == "") && (
                        <small className="p-error block">{`${utils.toPascalCase(options.field)} is required`}</small>
                    )}
                </>
            );
        }
        return (
            <div key={nanoid()}>
                {options.rowData?.file == null ? (
                    <div className="w-[30px] h-[30px] flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 rounded">
                        <FileText size={16} className="text-neutral-400" />
                    </div>
                ) : (
                    <img src={utils.appendBase64Data(options.rowData?.file)} alt="document" width="30px" />
                )}
            </div>
        );
    };

    onImageSelected = async (event: ChangeEvent<HTMLInputElement>, editorCallback: (val: any) => void) => {
        const documentFile = event.target.files?.[0];
        const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
        if (!documentFile) {
            return;
        }
        if (!utils.checkCharacterlength(documentFile!.name) && this.imageInput) {
            toastService.error("File Name Exceeds 128 Characters.", "File Name");
            this.imageInput.value = "";
        }

        if (!allowedTypes.includes(documentFile.type)) {
            toastService.error("Only JPEG and PNG files are allowed.", "File Type");
            return;
        }
        const base64 = await utils.convertFileToBase64(documentFile);
        editorCallback(base64);
        this.setState({ showFile: utils.appendBase64Data(base64 as string, documentFile.type), fileType: documentFile.type });
    };

    confirmDeleteDocument = async (rowData: MerchantDocument, rowIndex: number) => {
        if (this.props.localMode) {
            const documents = this.state.documents;
            const index = rowIndex;
            if (index >= 0) {
                documents.splice(index, 1);
            }

            this.setState({ documents: documents, documentInvalid: false, showFile: null, addedDocumentInvalid: false });
            this.props.onLocalDocumentUpdated!(this.state.documents);
            return;
        }
        if (rowData.id === 0) {
            const updatedDocs = this.state.documents.filter((document, index) => index !== rowIndex);
            this.setState({
                documents: updatedDocs,
                documentInvalid: false,
                showFile: null,
                addedDocumentInvalid: false
            });

            return;
        }

        confirmDanger(
            "This action will remove the document and is irreversible. Are you sure you want to proceed?",
            "Delete Document",
            () => this.deleteDocument(rowData),
        );
    };

    deleteDocument = async (rowData: MerchantDocument) => {
        this.setState({ isLoading: true });
        const deleteResult = await merchantDocumentApiService.delete(rowData.id);

        if (deleteResult.success) {
            this.setState({ isLoading: false });
            this.setState({ documents: this.state.documents.filter((document) => document.id !== rowData.id) });
        } else {
            this.setState({ isLoading: false });
            toastService.error(deleteResult.message);
        }
    };

    downloadDocuments = async (fileName: string) => {
        const url = await this.loadFile(fileName);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "document.pdf");
        document.body.appendChild(link);
        link.click();
        link.parentNode!.removeChild(link);
        window.URL.revokeObjectURL(url);
        return "ok";
    };

    documentTypeBodyTemplate = (rowData: MerchantDocument): string => {
        const list = _.filter(this.documentTypes, (c) => c.value == rowData.documentType);
        return list.length > 0 ? list[0].label : "N/A";
    };

    documentStatusBodyTemplate = (rowData: MerchantDocument): string => {
        const list = _.filter(this.documentStatus, (c) => c.value == rowData.status);
        return list.length > 0 ? list[0].label : "N/A";
    };

    actionBodyTemplate = (rowData: MerchantDocument, index: any) => {
        return (
            <div key={nanoid()}>
                <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-md p-2 hover:bg-neutral-100 transition-colors"
                    tabIndex={0}
                    onClick={() => this.confirmDeleteDocument(rowData, index.rowIndex)}
                >
                    <X size={16} className="cursor-pointer text-neutral-500 hover:text-danger-500 transition-colors" />
                </button>
            </div>
        );
    };

    documentFileTemplate = (rowData: MerchantDocument) => {
        const isPdf = rowData.fileType?.includes("pdf") || rowData.fileStorageId?.toLowerCase().endsWith(".pdf");

        const imageSrc =
            this.state.imageSourceDocument.get(rowData.fileStorageId || "") ||
            (rowData.fileData ? utils.appendBase64Data(rowData.fileData, rowData.fileType) : undefined);

        const containerStyle: React.CSSProperties = {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
        };

        if (isPdf) {
            return (
                <div style={containerStyle}>
                    <button
                        className="btn card-subtitle btn-text-primary border-0 pl-0"
                        onClick={() => this.downloadDocuments(rowData.fileStorageId!)}
                        name="Document"
                    >
                        <FileText size={50} />
                    </button>
                </div>
            );
        }

        return (
            <div style={containerStyle}>
                <ExtendedImage
                    width="45px"
                    src={imageSrc || ""}
                    alt={rowData.fileName || rowData.fileStorageId || "Document"}
                    preview
                    downloadable={!this.props.localMode}
                    onPreview={async () => {
                        if (imageSrc) return imageSrc;

                        if (rowData.fileStorageId) {
                            const base64 = await this.loadFile(rowData.fileStorageId);
                            const updatedMap = new Map(this.state.imageSourceDocument);
                            updatedMap.set(rowData.fileStorageId, base64);
                            this.setState({ imageSourceDocument: updatedMap });
                            return base64;
                        }
                        return "";
                    }}
                />
            </div>
        );
    };

    isForSigningEditor = (options: ColumnEditorOptions) => {
        return (
            <div className="p-field-checkbox">
                <Checkbox
                    checked={options.value}
                    onChange={(e: CheckboxChangeEvent) => {
                        options.editorCallback?.(e.checked);
                    }}
                />
            </div>
        );
    };

    signOrderDocumentBodyTemplate = (rowData: MerchantDocument): string => {
        if (this.signOrderDocument.length != 0) {
            const list = _.filter(this.signOrderDocument, (c) => c.value == rowData.signOrder.toString());
            return list.length > 0 ? list[0].label : "0";
        }
        else {
            return "0";
        }
    };

    handleSignOrderDocument = (options: ColumnEditorOptions) => {
        return (
            <div key={nanoid()}>
                <Dropdown
                    value={String(options.value)}
                    options={this.signOrderDocument}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => options.editorCallback && options.editorCallback(e.value)}
                    placeholder="Select a Order"
                    itemTemplate={(option) => {
                        return <span>{option.label}</span>;
                    }}
                />
                {this.state.documentInvalid && options.value === 0 && (
                    <>
                        <br></br>
                        <small className="p-error block">{`${utils.toPascalCase(options.field)} is required`}</small>
                    </>
                )}
            </div>
        );

    };

    render() {
        return (
            <div>
                <DataTable
                                        header={this.renderHeader}
                                        editMode="row"
                                        onRowEditComplete={this.onRowEditComplete}
                                        value={this.state.documents}
                                        loading={this.state.isLoading}
                                        rowEditValidator={this.onRowValidateDocument}
                                    >
                                        <Column field="id" header="Id" style={{ minWidth: "50px" }}></Column>
                                        <Column
                                            field="fileName"
                                            header="File Name"
                                            editor={(options) => this.textEditor(options)}
                                            style={{ minWidth: "50px" }}
                                        ></Column>
                                        <Column
                                            field="description"
                                            header="Description"
                                            editor={(options) => this.textEditor(options)}
                                            style={{ minWidth: "50px" }}
                                        ></Column>
                                        <Column
                                            field="documentType"
                                            header="Type"
                                            body={this.documentTypeBodyTemplate}
                                            editor={(options) => this.typesEditor(options)}
                                            style={{ minWidth: "50px" }}
                                        ></Column>
                                        <Column
                                            field="isForSigning"
                                            header="Is For Signing"
                                            editor={(options) => this.isForSigningEditor(options)}
                                            body={(rowData) => (
                                                <Checkbox checked={rowData.isForSigning} disabled />
                                            )}
                                            style={{ minWidth: "50px" }}
                                        />
                                        <Column
                                            field="signOrder"
                                            header="Sign Order"
                                            editor={(options) => this.handleSignOrderDocument(options)}
                                            body={this.signOrderDocumentBodyTemplate}
                                            style={{ minWidth: "50px" }}
                                        ></Column>

                                        <Column
                                            field="status"
                                            header="Status"
                                            editor={(options) => this.statusEditor(options)}
                                            body={this.documentStatusBodyTemplate}
                                            style={{ minWidth: "50px" }}
                                        ></Column>
                                        <Column
                                            field="file"
                                            header="File"
                                            body={this.documentFileTemplate}
                                            editor={(options) => this.fileEditor(options)}
                                            style={{ minWidth: "50px" }}
                                        ></Column>
                                        <Column
                                            rowEditor
                                            headerStyle={{ width: "10%", minWidth: "8rem" }}
                                            bodyStyle={{ textAlign: "center" }}
                                        ></Column>
                                        <Column
                                            header=""
                                            body={this.actionBodyTemplate}
                                            headerStyle={{ width: "8em", textAlign: "center" }}
                                            bodyStyle={{ textAlign: "center", overflow: "visible" }}
                                        ></Column>
                                    </DataTable>
                    {this.props.localMode && this.props.localDocumentInvalid && (
                        <strong className="p-error block m-2">Document information is not valid.</strong>
                    )}
            </div>
        );
    }
}
